import { isEmpty, without, isInteger, size } from 'lodash';
import React from 'react';
import styled from 'styled-components';

import Button from '../../../Button';
import Checkbox from '../../../Checkbox';
import Modal from '../../../Modal';
import TicketFilterByMailGroup from '../TicketFilterByMailGroup';
import TicketFilterByManager from '../TicketFilterByManager';
import TicketFilterByOrderStatus from '../TicketFilterByOrderStatus';
import TicketFilterByTag from '../TicketFilterByTag';
import TicketFilterByType from '../TicketFilterByType';
import TicketFilterByDate from '../TicketFilterByDate';
import TicketFilterByStatus from '../TicketFilterByStatus';
import TicketNoOrderFilter from '../TicketNoOrderFilter';

import {
  SortBox as SortBoxIcon,
  Filter as FilterIcon,
  TicketFiltersClose as CloseIcon,
} from '@icons/common';

import { formatDecimal, pluralize } from '../../../../utils/userflow';
import { ValueOf } from '@utils/tsUtils';
import {
  CLOSED_REASON_STATUSES,
  FILTER_NAMES,
  FILTER_PLURALIZE,
  FILTER_TABS,
  TICKET_PLURALIZE,
} from './constants';
import {
  ActualFiltersType,
  FiltersType,
  InitialFiltersType,
  SortType,
} from './types';
import { TypeType } from '../TicketFilterByType/TicketFilterByType';
import { StatusType } from '../TicketFilterByStatus/TicketFilterByStatus';
import { ManagerType } from '../TicketFilterByManager/TicketFilterByManager';
import { GeneralManagerType, MailGroupType, TagType } from 'types/types';

const { StyledWrapper: StyledCheckboxWrapper } = Checkbox;

export interface TicketFiltersProps {
  className?: string;
  activeTicketsTab: ValueOf<typeof FILTER_TABS>;
  filters: FiltersType;
  initialFilters: InitialFiltersType;
  mailGroups?: MailGroupType[] | null;
  managers?: ManagerType[] | null;
  statuses?: StatusType[] | null;
  tags?: TagType[] | null;
  types?: TypeType[] | null;
  isLoadingList?: boolean;
  currentManager: GeneralManagerType;
  counts?: {
    total: number;
    unassigned: number;
  };
  sort?: SortType;

  onSort: (sort: SortType) => void;
  onClear: () => void;
  onFilter: (filters: ActualFiltersType) => void;
  onToggle: (isOpen: boolean) => void;
  onChangeTicketsTab: (tab: ValueOf<typeof FILTER_TABS>) => void;
}

interface TicketFiltersState {
  isOpenFilters: boolean;
  editableFilters: FiltersType;
}

class TicketFilters extends React.Component<
  TicketFiltersProps,
  TicketFiltersState
> {
  static defaultProps = {
    filters: {},
    initialFilters: {},
    mailGroups: null,
    managers: null,
    onClear: Function.prototype,
    onFilter: Function.prototype,
    onToggle: Function.prototype,
    statuses: null,
    tags: null,
    types: null,
    sort: 'DESC',
    counts: {
      total: 0,
      unassigned: 0,
    },
    isLoadingList: false,
    activeTicketsTab: FILTER_TABS.ACTUAL,
    onChangeTicketsTab: Function.prototype,
  };

  static getContentRenderer({
    editableFilters,
    managers,
    onClear,
    onClose,
    onFilter,
    onClearFilter,
    onCheckoutFilter,
    onFilterByName,
    onSubmit,
    statuses,
    mailGroups,
    tags,
    types,
    currentManager,
    ...props
  }) {
    return () => (
      <StyledContent {...props} onSubmit={onSubmit}>
        <StyledCloseButton functional onClick={onClose} title="Закрыть фильтры">
          <CloseIcon />
        </StyledCloseButton>
        <StyledBody>
          <StyledTitle>Фильтры</StyledTitle>
          <StyledFilters>
            <StyledFiltersColumn>
              <td width="250px">
                <StyledTableTitle>Дата вылета</StyledTableTitle>
                <StyledTableRow>
                  <TicketFilterByDate
                    name={FILTER_NAMES.DEPARTURE_DATE}
                    onChange={onFilterByName}
                    value={editableFilters[FILTER_NAMES.DEPARTURE_DATE]}
                  />
                </StyledTableRow>
                {statuses && !isEmpty(statuses) && (
                  <StyledStatusFilter>
                    <StyledTableTitle>Статусы тикета</StyledTableTitle>
                    <StyledTableRow>
                      <TicketFilterByStatus
                        name={FILTER_NAMES.STATUSES}
                        onChange={onCheckoutFilter}
                        onClear={onClearFilter}
                        statuses={statuses}
                        value={editableFilters[FILTER_NAMES.STATUSES]}
                        closedReasonName={FILTER_NAMES.CLOSE_REASON_STATUSES}
                        closedReasonValue={
                          editableFilters[FILTER_NAMES.CLOSE_REASON_STATUSES]
                        }
                        closedReasonStatuses={CLOSED_REASON_STATUSES}
                      />
                    </StyledTableRow>
                  </StyledStatusFilter>
                )}
                <StyledExtraFilters>
                  <StyledTableTitle>Дополнительные фильтры</StyledTableTitle>
                  <StyledTableRow>
                    <TicketNoOrderFilter
                      name={FILTER_NAMES.ORDERS}
                      onChange={onCheckoutFilter}
                      value={editableFilters[FILTER_NAMES.ORDERS]}
                    />
                  </StyledTableRow>
                </StyledExtraFilters>
              </td>
              <td>
                {types && !isEmpty(types) && (
                  <>
                    <StyledTableTitle>Тип тикета</StyledTableTitle>
                    <StyledTableRow>
                      <TicketFilterByType
                        name={FILTER_NAMES.TYPES}
                        onChange={onFilter}
                        types={types}
                        value={editableFilters[FILTER_NAMES.TYPES]}
                      />
                    </StyledTableRow>
                  </>
                )}
                {mailGroups && !isEmpty(mailGroups) && (
                  <>
                    <StyledTableTitle>Группы</StyledTableTitle>
                    <StyledTableRow>
                      <TicketFilterByMailGroup
                        name={FILTER_NAMES.MAIL_GROUPS}
                        onChange={onFilter}
                        mailGroups={mailGroups}
                        value={editableFilters[FILTER_NAMES.MAIL_GROUPS]}
                        currentManager={currentManager}
                      />
                    </StyledTableRow>
                  </>
                )}
                {tags && !isEmpty(tags) && (
                  <>
                    <StyledTableTitle>Теги</StyledTableTitle>
                    <StyledTableRow>
                      <TicketFilterByTag
                        name={FILTER_NAMES.TAGS}
                        dark
                        onChange={onFilter}
                        tags={tags}
                        value={editableFilters[FILTER_NAMES.TAGS]}
                      />
                    </StyledTableRow>
                  </>
                )}
                {managers && !isEmpty(managers) && (
                  <>
                    <StyledTableTitle>Ответственный</StyledTableTitle>
                    <StyledTableRow>
                      <TicketFilterByManager
                        managers={managers}
                        name={FILTER_NAMES.MANAGERS}
                        onChange={onFilter}
                        value={editableFilters[FILTER_NAMES.MANAGERS]}
                      />
                    </StyledTableRow>
                  </>
                )}
                <>
                  <StyledTableTitle>Статус заказа</StyledTableTitle>
                  <StyledTableRow>
                    <TicketFilterByOrderStatus
                      name={FILTER_NAMES.ORDER_STATUSES}
                      onChange={onFilter}
                      value={editableFilters[FILTER_NAMES.ORDER_STATUSES]}
                    />
                  </StyledTableRow>
                </>
              </td>
            </StyledFiltersColumn>
          </StyledFilters>
        </StyledBody>
        <StyledControls>
          <StyledClear onClick={onClear}>Фильтры по умолчанию</StyledClear>
          <StyledSubmit primary type="submit">
            Применить
          </StyledSubmit>
        </StyledControls>
      </StyledContent>
    );
  }

  static getHeaderRenderer({
    onClear,
    onSort,
    sort,
    onClickFilter,
    onClickActual,
    onClickPlanned,
    activeTicketsTab,
    isLoadingList,
    counts,
    filters,
    ...props
  }) {
    const { total, unassigned } = counts;
    const actualFilters = filters[FILTER_TABS.ACTUAL];
    const isEmptyActualFilters = isEmpty(actualFilters);
    const isValidCount = isInteger(total) && total > 0;
    const checkedSort = sort !== 'DESC';
    const mailGroupsSelected =
      !!actualFilters[FILTER_NAMES.MAIL_GROUPS]?.length;

    return () => (
      <StyledHeader {...props}>
        <StyledHeaderCategory>
          <StyledTab
            onClick={onClickActual}
            isActive={activeTicketsTab === FILTER_TABS.ACTUAL}
          >
            <StyledFilterButton
              isFiltered={!isEmptyActualFilters}
              onClick={onClickFilter}
              disabled={activeTicketsTab === FILTER_TABS.PLANNED}
            >
              <FilterIcon />
            </StyledFilterButton>
            {isEmptyActualFilters ? (
              <span>Актуальное</span>
            ) : (
              <>
                {size(actualFilters)}{' '}
                {pluralize(size(actualFilters), FILTER_PLURALIZE)}
                <StyledClearHeaderFilters
                  onClick={onClear}
                  disabled={activeTicketsTab === FILTER_TABS.PLANNED}
                >
                  <CloseIcon />
                </StyledClearHeaderFilters>
              </>
            )}
          </StyledTab>
          <StyledTab
            isActive={activeTicketsTab === FILTER_TABS.PLANNED}
            onClick={onClickPlanned}
          >
            В плане
          </StyledTab>
          <StyledSortCheckboxContainer>
            <StyledSortCheckbox
              type="checkbox"
              checked={checkedSort}
              onChange={onSort}
            />
            <StyledSortBoxIcon width={17} />
          </StyledSortCheckboxContainer>
        </StyledHeaderCategory>
        <StyledFooter>
          {isValidCount && (
            <StyledTotal>
              {`Показаны ${formatDecimal(total)} ${pluralize(
                total,
                TICKET_PLURALIZE
              )}`}
            </StyledTotal>
          )}
          {!isLoadingList && mailGroupsSelected && (
            <StyledTotal>В очереди {formatDecimal(unassigned)}</StyledTotal>
          )}
        </StyledFooter>
      </StyledHeader>
    );
  }

  private el: React.RefObject<HTMLDivElement>;
  private dropdownRef: React.RefObject<HTMLDivElement>;

  constructor(props) {
    super(props);
    this.state = {
      isOpenFilters: false,
      editableFilters: {
        [FILTER_TABS.ACTUAL]:
          props.filters[FILTER_TABS.ACTUAL] ||
          props.initialFilters[FILTER_TABS.ACTUAL],
        [FILTER_TABS.PLANNED]:
          props.filters[FILTER_TABS.PLANNED] ||
          props.initialFilters[FILTER_TABS.PLANNED],
      },
    };

    this.el = React.createRef();
    this.dropdownRef = React.createRef();

    this.handleClear = this.handleClear.bind(this);
    this.handleClearEditableFilters =
      this.handleClearEditableFilters.bind(this);
    this.handleClearEditableFilter = this.handleClearEditableFilter.bind(this);
    this.handleFilterByName = this.handleFilterByName.bind(this);
    this.handleFilter = this.handleFilter.bind(this);
    this.handleCheckoutFilter = this.handleCheckoutFilter.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleToggleFilters = this.handleToggleFilters.bind(this);
    this.handleChangeTicketsTab = this.handleChangeTicketsTab.bind(this);
    this.handleClickActualTab = this.handleClickActualTab.bind(this);
    this.handleClickPlannedTab = this.handleClickPlannedTab.bind(this);
    this.handleSort = this.handleSort.bind(this);
    this.handleCloseFiltersModal = this.handleCloseFiltersModal.bind(this);
  }

  handleClear(event) {
    event.preventDefault();
    event.stopPropagation();
    const { initialFilters, onClear } = this.props;

    this.setState(
      prevProps => ({
        editableFilters: {
          ...prevProps.editableFilters,
          [FILTER_TABS.ACTUAL]: initialFilters[FILTER_TABS.ACTUAL],
        },
      }),
      () => {
        this.handleToggleFilters(false);
        onClear();
      }
    );
  }

  handleClearEditableFilters(event) {
    event.stopPropagation();
    const editableFilters = {};
    const { initialFilters } = this.props;

    this.setState(prevState => ({
      editableFilters: {
        ...prevState.editableFilters,
        [FILTER_TABS.ACTUAL]: {
          ...initialFilters[FILTER_TABS.ACTUAL],
          ...editableFilters,
        },
      },
    }));
  }

  handleClearEditableFilter(name) {
    const { editableFilters: currentEditableFilters } = this.state;
    const editableFilters = {
      ...currentEditableFilters[FILTER_TABS.ACTUAL],
    };

    delete editableFilters[name];

    this.setState(prevState => ({
      editableFilters: {
        ...prevState.editableFilters,
        [FILTER_TABS.ACTUAL]: {
          ...editableFilters,
        },
      },
    }));
  }

  handleFilterByName({ name, value, tab = FILTER_TABS.ACTUAL }) {
    this.setState(({ editableFilters }) => {
      const tabFilters = editableFilters[tab];
      return {
        editableFilters: {
          ...editableFilters,
          [tab]: {
            ...tabFilters,
            [name]: value,
          },
        },
      };
    });
  }

  handleFilter({ target }) {
    const { name, value } = target;

    const { activeTicketsTab } = this.props;
    const { editableFilters } = this.state;
    const currentEditableFilters = editableFilters[activeTicketsTab];

    this.setState({
      editableFilters: {
        ...editableFilters,
        [activeTicketsTab]: {
          ...currentEditableFilters,
          [name]: value.length ? value : undefined,
        },
      },
    });
  }

  handleCheckoutFilter({ target }) {
    const { name, value } = target;
    const { activeTicketsTab } = this.props;
    const { editableFilters } = this.state;

    const currentEditableFilters = editableFilters[activeTicketsTab];

    let newValue;

    if (value) {
      const editableStatusFilters = currentEditableFilters[name] || [];

      if (editableStatusFilters.includes(value)) {
        newValue = without(editableStatusFilters, value);
      } else {
        newValue = editableStatusFilters.concat(value);
      }

      if (isEmpty(newValue)) {
        newValue = undefined;
      }
    }

    this.setState({
      editableFilters: {
        ...editableFilters,
        [activeTicketsTab]: {
          ...currentEditableFilters,
          [name]: newValue,
        },
      },
    });
  }

  handleSort({ target }) {
    const { onSort } = this.props;
    const { checked } = target;

    onSort(checked ? 'ASC' : 'DESC');
  }

  handleSubmit(event) {
    event.preventDefault();

    const { onFilter } = this.props;
    const { editableFilters } = this.state;
    const actualFilters = editableFilters[FILTER_TABS.ACTUAL];
    this.handleToggleFilters(false);
    onFilter(actualFilters);
  }

  handleToggleFilters(isOpen) {
    const { onToggle, initialFilters, filters } = this.props;

    this.setState(
      () => ({
        isOpenFilters: isOpen,
        editableFilters: {
          [FILTER_TABS.ACTUAL]:
            filters[FILTER_TABS.ACTUAL] || initialFilters[FILTER_TABS.ACTUAL],
          [FILTER_TABS.PLANNED]:
            filters[FILTER_TABS.PLANNED] || initialFilters[FILTER_TABS.PLANNED],
        },
      }),
      () => {
        onToggle(isOpen);
      }
    );
  }

  handleChangeTicketsTab(tab) {
    const { activeTicketsTab, onChangeTicketsTab } = this.props;

    if (activeTicketsTab === tab) return;

    onChangeTicketsTab(tab);
  }

  handleClickActualTab() {
    const { activeTicketsTab, onFilter, filters } = this.props;

    if (activeTicketsTab === FILTER_TABS.ACTUAL) return;
    const actualFilters = filters[FILTER_TABS.ACTUAL];
    this.handleChangeTicketsTab(FILTER_TABS.ACTUAL);
    onFilter(actualFilters);
  }

  handleClickPlannedTab() {
    const { activeTicketsTab, onFilter, currentManager } = this.props;

    if (activeTicketsTab === FILTER_TABS.PLANNED) return;
    this.handleChangeTicketsTab(FILTER_TABS.PLANNED);
    onFilter({ statuses: ['planned'], managers: [currentManager.id] });
  }

  handleCloseFiltersModal() {
    const { filters, initialFilters } = this.props;
    this.handleToggleFilters(false);

    this.setState(prevState => ({
      editableFilters: {
        ...prevState.editableFilters,
        [FILTER_TABS.ACTUAL]:
          filters[FILTER_TABS.ACTUAL] || initialFilters[FILTER_TABS.ACTUAL],
      },
    }));
  }

  render() {
    const {
      managers,
      statuses,
      tags,
      types,
      counts,
      mailGroups,
      activeTicketsTab,
      filters,
      sort,
      isLoadingList,
      currentManager,
      ...props
    } = this.props;
    const { editableFilters, isOpenFilters } = this.state;

    const baseProps = {
      managers,
      onFilterByName: this.handleFilterByName,
      onFilter: this.handleFilter,
      onClearFilter: this.handleClearEditableFilter,
      onCheckoutFilter: this.handleCheckoutFilter,
      activeTicketsTab,
      statuses,
      tags,
      types,
      mailGroups,
    };

    const contentProps = {
      ...baseProps,
      editableFilters: editableFilters[FILTER_TABS.ACTUAL],
      currentManager,
      onClear: this.handleClearEditableFilters,
      onClose: this.handleCloseFiltersModal,
      onSubmit: this.handleSubmit,
    };

    const headerProps = {
      ...baseProps,
      filters,
      counts,
      sort,
      editableFilters,
      isLoadingList,
      onClear: this.handleClear,
      onClickFilter: event => {
        event.stopPropagation();
        this.handleToggleFilters(true);
      },
      onSort: this.handleSort,
      onClickActual: this.handleClickActualTab,
      onClickPlanned: this.handleClickPlannedTab,
    };

    return (
      <StyledTicketFilters {...props} ref={this.el}>
        <StyledHeader>
          {TicketFilters.getHeaderRenderer(headerProps)()}
        </StyledHeader>
        {isOpenFilters && (
          <StyledFiltersModal ref={this.dropdownRef}>
            {React.cloneElement(
              TicketFilters.getContentRenderer(contentProps)(),
              { tabIndex: 0 }
            )}
          </StyledFiltersModal>
        )}
      </StyledTicketFilters>
    );
  }
}

const StyledTicketFilters = styled.div`
  position: relative;
  width: 100%;
`;

const StyledFiltersModal = styled(Modal)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledHeader = styled.div`
  border-radius: 4px;
  width: 100%;
`;

const StyledTitle = styled.div`
  align-items: center;
  display: flex;
  color: #17181a;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 16px;
`;

const StyledTab = styled.div<{ isActive: boolean }>`
  align-items: center;
  justify-content: center;
  display: flex;
  width: 100%;
  cursor: pointer;
  white-space: nowrap;
  color: #17181a;
  font-size: 16px;
  line-height: 20px;
  font-weight: ${({ isActive }) => (isActive ? 'bold' : 'normal')};
  background-color: ${({ isActive }) => (isActive ? 'white' : 'transparent')};
  padding: 5px 15px;
  border-radius: 16px;
`;

const StyledFilterButton = styled.button<{ isFiltered: boolean }>`
  display: flex;
  margin-right: 4px;
  border: none;
  outline: none;
  background: transparent;
  cursor: pointer;

  svg {
    height: 16px;
    width: 16px;

    .border {
      stroke: ${({ isFiltered }) => (isFiltered ? '#d0021b' : '#007aff')};
    }

    .filled {
      fill: ${({ isFiltered }) => (isFiltered ? '#d0021b' : 'transparent')};
    }
  }

  :disabled {
    svg {
      .border {
        stroke: #4d4d4d;
      }

      .filled {
        fill: #4d4d4d;
      }
    }
  }
`;

const StyledButton = styled(Button)`
  width: 178px;
  outline: none;
  border: none;
`;

const StyledClear = styled.button`
  font-size: 14px;
  cursor: pointer;
  color: #d0021b;
  background-color: transparent;
  border: none;
  outline: none;
`;

const StyledHeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: 10px;
  justify-content: space-between;
`;

const StyledClearHeaderFilters = styled.button`
  display: flex;
  margin-right: 4px;
  border: none;
  outline: none;
  background: transparent;
  cursor: pointer;

  svg {
    width: 8px;
    height: 8px;
    margin-top: 3px;
    margin-left: 5px;
  }
`;

const StyledHeaderCategory = styled(StyledHeaderContainer)`
  min-height: 37px;
`;

const StyledFooter = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  justify-content: center;
`;

const StyledTotal = styled.div`
  color: #93969b;
  font-size: 12px;
  line-height: 16px;
  font-weight: normal;
`;

const StyledBody = styled.div`
  min-height: 150px;
`;

const StyledControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
`;

const StyledContent = styled.form`
  background-color: #fff;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  padding: 16px;
  z-index: 2;
  width: 750px;
  position: relative;
`;

const StyledCloseButton = styled(Button)`
  position: absolute;
  top: 16px;
  right: 16px;

  svg {
    height: 12px;
    width: 12px;
  }
`;

const StyledFiltersColumn = styled.table`
  border-collapse: separate;
  table-layout: fixed;
  width: 100%;
  color: #4d4d4d;
  font-size: 14px;

  td {
    padding-right: 16px;

    &:last-of-type {
      padding-right: 0;
    }
  }
`;

const StyledTableRow = styled.div`
  display: inline-block;
  width: 100%;
  margin-bottom: 16px;

  :last-of-type {
    margin-bottom: 0;
  }
`;

const StyledTableTitle = styled.div`
  display: inline-block;
  width: 100%;
  font-weight: 700;
  margin-bottom: 8px;
`;

const StyledFilters = styled.div`
  display: flex;
`;

const StyledStatusFilter = styled(StyledTableRow)`
  td:last-of-type {
    color: #17181a;
    font-size: 12px;
  }

  li + li {
    margin-top: 10px;
  }

  ${StyledCheckboxWrapper} {
    margin-right: 4px;
  }
`;

const StyledExtraFilters = styled(StyledTableRow)`
  td:last-of-type {
    color: #17181a;
    font-size: 12px;
  }

  li + li {
    margin-top: 10px;
  }

  ${StyledCheckboxWrapper} {
    margin-right: 4px;
  }
`;

const StyledSubmit = styled(StyledButton)`
  height: 40px;
  width: 100px;
  background: #007aff;
  color: white;
  font-weight: bold;
  border-radius: 4px;
  padding: 11px 12px 13px;

  :hover {
    background: #007aff;
  }
`;

const StyledSortBoxIcon = styled(SortBoxIcon)``;

const StyledSortCheckbox = styled.input`
  background: transparent;
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
`;

const StyledSortCheckboxContainer = styled.label`
  display: flex;
  align-items: center;
  position: relative;
  padding-left: 10px;
  cursor: pointer;
  user-select: none;

  & > ${StyledSortCheckbox}:checked ~ ${StyledSortBoxIcon} {
    position: relative;
    top: 2px;
    transform: rotateX(180deg);
  }
`;

export default TicketFilters;
